import { RadioGroup } from "@headlessui/react"
import { OptionValue, ProductOption } from "@lib/swell/storefront-data-hooks/src/types"
import { classNames } from "core/utils"
import { useFormikContext, useField } from "formik"
import Image from "next/image"
import { useState, useEffect } from "react"
import { getPrice } from "@lib/swell/storefront-data-hooks/src/utils/product"

interface SelectWithImageFieldProps {
  option: ProductOption
  updateSelectionChange: any
}
const SelectWithImageField = ({ option, updateSelectionChange }: SelectWithImageFieldProps) => {
  const { name, values, description } = option
  const { isSubmitting } = useFormikContext()
  const [input] = useField(name)
  const [currentImageUrl, setCurrentImageUrl] = useState(option.image.url)

  const imageSizeClasses = () => {
    switch (option.image_size) {
      case "xs":
        return "sm:aspect-w-5 sm:aspect-h-2"
      case "s":
        return "sm:aspect-w-5 sm:aspect-h-3"
      case "md":
        return "sm:aspect-w-5 sm:aspect-h-4"
      case "lg":
        return "sm:aspect-w-5 sm:aspect-h-5"
      default:
        return "sm:aspect-w-5 sm:aspect-h-3"
    }
  }
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-gray-300">
      <div
        className={classNames(
          "aspect-h-1 aspect-w-2 overflow-hidden rounded-t-lg",
          imageSizeClasses()
        )}
      >
        <Image
          quality="85"
          src={currentImageUrl}
          alt={option.name}
          className="object-cover object-center"
          loading="lazy"
          fill={true}
        />
      </div>

      <div className="mt-4 p-4">
        <RadioGroup
          {...input}
          disabled={isSubmitting}
          onChange={(value: string) => {
            input.onChange({ target: { value, name } })
            const optionValue = values.find((optionValue) => optionValue.name == value)
            setCurrentImageUrl(optionValue?.option_value_image?.url || option.image.url)
            updateSelectionChange(name, optionValue?.id, value)
          }}
          as="div"
          className="flex flex-col justify-between space-y-4"
        >
          <span className="flex grow flex-col space-y-2">
            <RadioGroup.Label as="span" className="text-sm font-medium text-gray-900" passive>
              {name}
            </RadioGroup.Label>

            <RadioGroup.Description as="span" className="text-sm text-gray-500">
              {description}
            </RadioGroup.Description>
          </span>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {values.map((optionValue: OptionValue) => (
              <RadioGroup.Option
                as="div"
                key={optionValue.id}
                value={optionValue.name}
                className={({ active }) =>
                  classNames(
                    active ? "ring-2 ring-primary-500" : "",
                    "relative block cursor-pointer rounded-lg border border-gray-300 p-4 focus:outline-hidden"
                  )
                }
              >
                {({ active, checked }) => (
                  <>
                    <RadioGroup.Label
                      as="p"
                      className="flex items-center justify-between text-base font-medium text-gray-900"
                    >
                      <span>{optionValue.name}</span>{" "}
                      <span className="text-xs text-gray-500">
                        {optionValue?.price ? getPrice(optionValue?.price, "usd") : "Included"}
                      </span>
                    </RadioGroup.Label>
                    <RadioGroup.Description as="p" className="mt-1 text-sm text-gray-500">
                      {optionValue.description}
                    </RadioGroup.Description>
                    <div
                      className={classNames(
                        active ? "border" : "border-2",
                        checked ? "border-primary-500" : "border-transparent",
                        "pointer-events-none absolute -inset-px rounded-lg"
                      )}
                      aria-hidden="true"
                    />
                  </>
                )}
              </RadioGroup.Option>
            ))}
          </div>
        </RadioGroup>
      </div>
    </div>
  )
}
export default SelectWithImageField
